import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "专题", to: "/specials" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [mobileKeyword, setMobileKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goSearch = (value: string) => {
    const kw = value.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
    setMenuOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-300/60 bg-background-100/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-foreground-950">
            秋
          </span>
          <span className="font-heading text-xl font-semibold tracking-wide text-foreground-950">
            秋霞<span className="text-primary-500">网</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`relative text-sm font-medium transition-colors cursor-pointer ${
                isActive(item.to)
                  ? "text-primary-500"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {item.label}
              {isActive(item.to) && (
                <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded-full bg-primary-500" />
              )}
            </Link>
          ))}
        </nav>

        <form
          className="hidden items-center gap-2 md:flex"
          onSubmit={(e) => {
            e.preventDefault();
            goSearch(keyword);
          }}
        >
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧"
              className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-56"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-900 hover:bg-background-200 md:hidden cursor-pointer"
        >
          <i className="ri-menu-line text-xl" />
        </button>
      </div>

      <form
        className="flex items-center gap-2 px-4 pb-3 md:hidden"
        onSubmit={(e) => {
          e.preventDefault();
          goSearch(mobileKeyword);
        }}
      >
        <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
          <i className="ri-search-line text-sm text-foreground-400" />
          <input
            type="text"
            value={mobileKeyword}
            onChange={(e) => setMobileKeyword(e.target.value)}
            placeholder="搜索电影、电视剧"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
      </form>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-secondary-950/70"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full w-72 flex-col bg-background-100 animate-fade-in">
            <div className="flex h-16 items-center justify-between border-b border-background-300 px-5">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                导航菜单
              </span>
              <button
                type="button"
                aria-label="关闭菜单"
                onClick={() => setMenuOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <nav className="flex flex-col p-3">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.to)
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}